import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring } from "framer-motion";

const FINE_POINTER =
  typeof window !== "undefined" && window.matchMedia("(pointer: fine)").matches;

// Fish drawn facing right; nose sits at (34px, 14px) when rendered 44px wide.
const NOSE = { x: 34, y: 14 };

export function FishSvg({ width = 44, height = 28 }) {
  return (
    <svg viewBox="0 0 64 40" width={width} height={height} aria-hidden="true">
      <path d="M19 20C9 5 1 9 4 20c-3 11 5 15 15 0z" fill="#FF8C00" opacity=".9" />
      <path d="M24 13c4-9 17-9 22-1z" fill="#FF8C00" opacity=".85" />
      <path d="M24 27c4 9 17 9 22 1z" fill="#FF8C00" opacity=".85" />
      <ellipse cx="34" cy="20" rx="16" ry="9" fill="#FF6B00" />
      <ellipse cx="36" cy="23" rx="10" ry="4" fill="#FFB36B" opacity=".55" />
      <circle cx="40" cy="22.5" r="1.6" fill="#FF3D71" opacity=".45" />
      <circle cx="43" cy="18" r="3.2" fill="#fff" />
      <circle cx="44" cy="18" r="1.8" fill="#0A0A0A" />
      <circle cx="44.8" cy="17.2" r=".7" fill="#fff" />
      <path d="M47.5 21.5q2 1.2 3.2-.8" stroke="#0A0A0A" strokeWidth="1" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export default function FishCursor() {
  const x = useMotionValue(-200);
  const y = useMotionValue(-200);
  const sx = useSpring(x, { stiffness: 480, damping: 34, mass: 0.5 });
  const sy = useSpring(y, { stiffness: 480, damping: 34, mass: 0.5 });
  const flip = useSpring(1, { stiffness: 260, damping: 18 });
  const rot = useSpring(0, { stiffness: 220, damping: 20 });
  const scale = useSpring(1, { stiffness: 300, damping: 22 });

  const [label, setLabel] = useState("");
  const [hidden, setHidden] = useState(true);
  const [bubbles, setBubbles] = useState([]);
  const last = useRef({ x: 0, y: 0, bubbleAt: 0, dir: 1 });

  useEffect(() => {
    if (!FINE_POINTER) return undefined;
    document.documentElement.classList.add("has-fish-cursor");

    const onMove = (e) => {
      const dx = e.clientX - last.current.x;
      const dy = e.clientY - last.current.y;
      x.set(e.clientX);
      y.set(e.clientY);
      if (Math.abs(dx) > 2) {
        last.current.dir = dx > 0 ? 1 : -1;
        flip.set(last.current.dir);
      }
      rot.set(Math.max(-22, Math.min(22, dy * 1.4)));
      last.current.x = e.clientX;
      last.current.y = e.clientY;

      const now = performance.now();
      if (now - last.current.bubbleAt > 110 && Math.hypot(dx, dy) > 3) {
        last.current.bubbleAt = now;
        const id = now;
        setBubbles((b) => [
          ...b.slice(-7),
          { id, x: e.clientX - last.current.dir * 26, y: e.clientY + 2, s: 4 + Math.random() * 6 },
        ]);
      }

      const target = e.target instanceof Element ? e.target : null;
      const hide = target?.closest('[data-cursor="hide"], iframe');
      setHidden(!!hide);
      const interactive = target?.closest("a, button, [data-cursor-label], input, textarea, label");
      scale.set(interactive ? 1.28 : 1);
      setLabel(interactive?.closest("[data-cursor-label]")?.dataset.cursorLabel || "");
    };
    const onLeave = () => setHidden(true);
    const onEnter = () => setHidden(false);
    const onClick = () => setLabel("");

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("click", onClick, true);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    return () => {
      document.documentElement.classList.remove("has-fish-cursor");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("click", onClick, true);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
    };
  }, [x, y, flip, rot, scale]);

  if (!FINE_POINTER) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[200]" data-testid="fish-cursor" aria-hidden="true">
      <AnimatePresence>
        {bubbles.map((b) => (
          <motion.span
            key={b.id}
            initial={{ opacity: 0.7, scale: 0.4 }}
            animate={{ opacity: 0, scale: 1, y: -34 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            onAnimationComplete={() => setBubbles((list) => list.filter((i) => i.id !== b.id))}
            style={{ left: b.x, top: b.y, width: b.s, height: b.s }}
            className="absolute rounded-full border border-ember/70 bg-ember/10"
          />
        ))}
      </AnimatePresence>

      <motion.div
        style={{ x: sx, y: sy, opacity: hidden ? 0 : 1 }}
        className="absolute left-0 top-0 transition-opacity duration-200"
      >
        <motion.div style={{ scaleX: flip, scale, transformOrigin: `${NOSE.x}px ${NOSE.y}px` }}>
          <motion.div
            style={{ rotate: rot, transformOrigin: `${NOSE.x}px ${NOSE.y}px`, marginLeft: -NOSE.x, marginTop: -NOSE.y }}
          >
            <FishSvg />
          </motion.div>
        </motion.div>
        <AnimatePresence>
          {label && (
            <motion.span
              data-testid="fish-cursor-label"
              initial={{ opacity: 0, y: 4, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 4, scale: 0.9 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 top-5 -translate-x-1/2 whitespace-nowrap rounded-full bg-ember px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-black shadow-[0_0_20px_rgba(255,107,0,0.45)]"
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
